import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface Surcharge {
  code: string;
  chargeType: 'INCLUDE' | 'EXCLUDE';
  mandatory: boolean;
  price: {
    amount: number;
    currency: string;
  };
  description?: string;
}

interface HotelFareBreakdownModalProps {
  open: boolean;
  onClose: () => void;
  net: number;
  gross: number;
  finalPrice: number;
  markupIsFixed: boolean;
  markupValue: number;
  surcharges: Surcharge[] | null;
}

export const HotelFareBreakdownModal: React.FC<HotelFareBreakdownModalProps> = ({
  open,
  onClose,
  net,
  gross,
  finalPrice,
  markupIsFixed,
  markupValue,
  surcharges,
}) => {
  const includedSurcharges = surcharges ? surcharges.filter(s => s.chargeType === 'INCLUDE') : [];
  const excludedSurcharges = surcharges ? surcharges.filter(s => s.chargeType === 'EXCLUDE') : [];
  const markupAmount = finalPrice - gross;

  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} >
      <DialogTitle>{t('hotels.fareBreakDown.title')}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={1} minWidth="300px">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">{t('hotels.fareBreakDown.netAmount')}</Typography>
            <Typography variant="subtitle2" color="text.primary">{net?.toFixed(2) || 0}</Typography>
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">{t('hotels.fareBreakDown.grossAmount')}</Typography>
            <Typography variant="subtitle2" color="text.primary">{gross?.toFixed(2) || 0}</Typography>
          </Box>
          {includedSurcharges.map((s, i) => (
            <Box key={`included-${i}`} display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">{s.description || s.code} ({t('hotels.fareBreakDown.included')})</Typography>
              <Typography variant="subtitle2" color="text.primary">{s.price.amount?.toFixed(2) || 0}</Typography>
            </Box>
          ))}

          {excludedSurcharges.map((s, i) => (
            <Box key={`excluded-${i}`} display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">{s.description || s.code} ({t('hotels.fareBreakDown.excluded')})</Typography>
              <Typography variant="subtitle2" color="text.primary">{s.price.amount?.toFixed(2) || 0}</Typography>
            </Box>
          ))}

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">{t('hotels.fareBreakDown.markup')} ({markupIsFixed ? t('hotels.fareBreakDown.fixed') : `${markupValue}%`})</Typography>
            <Typography variant="subtitle2" color="text.primary">{markupAmount?.toFixed(2) || 0}</Typography>
          </Box>

          <Divider sx={{my:1}}  />

          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6Bold" color="text.secondary">{t('hotels.fareBreakDown.total')}</Typography>
            <Typography variant="h6Bold" color="text.primary">{finalPrice?.toFixed(2) || 0}</Typography>
          </Box>
        </Box>

      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
